/**
 * First-Time User Experience Handler
 *
 * Shows the welcome tour for new users:
 * 1. Checks if user has completed the tour
 * 2. Shows appropriate tour (mobile or desktop) if not completed
 * 3. Tracks completion in database for authenticated users
 * 4. Falls back to localStorage for anonymous users
 *
 * Note: Terms acceptance has been moved to the search page and is handled separately.
 *
 * Features:
 * - Responsive: Shows mobile tour on mobile devices, desktop tour on desktop
 * - Persistent: Remembers user's status across sessions
 * - Graceful degradation: Works for both authenticated and anonymous users
 *
 * @author Ellis Reed
 */

"use client";

import React, { useState, useEffect, useCallback } from "react";
import { useSession } from "next-auth/react";
import { MobileWelcomeTour } from "@/components/tour/mobile-welcome-tour";
import { WelcomeTour } from "@/components/tour/welcome-tour";
import {
  getUserPreferences,
} from "@/app/actions/user-preferences";

type FlowState =
  | "loading"
  | "show-mobile-tour"
  | "show-desktop-tour"
  | "complete";

interface FirstTimeUserExperienceProps {
  children?: React.ReactNode;
}

// Local storage keys (terms now handled on search page)
const STORAGE_KEYS = {
  desktopTourCompleted: "usland-tour-completed",
  desktopTourSkipped: "usland-tour-skipped",
  mobileTourCompleted: "usland-mobile-tour-completed",
  mobileTourSkipped: "usland-mobile-tour-skipped",
};

export function FirstTimeUserExperience({
  children,
}: FirstTimeUserExperienceProps) {
  const { data: session, status: sessionStatus } = useSession();
  const [flowState, setFlowState] = useState<FlowState>("loading");
  const [isMobile, setIsMobile] = useState<boolean | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Check local storage for anonymous users
  const checkLocalStorage = useCallback(() => {
    if (typeof window === "undefined") return null;

    const desktopCompleted = localStorage.getItem(
      STORAGE_KEYS.desktopTourCompleted
    );
    const desktopSkipped = localStorage.getItem(
      STORAGE_KEYS.desktopTourSkipped
    );
    const mobileCompleted = localStorage.getItem(
      STORAGE_KEYS.mobileTourCompleted
    );
    const mobileSkipped = localStorage.getItem(STORAGE_KEYS.mobileTourSkipped);

    return {
      desktopTourDone: desktopCompleted === "true" || desktopSkipped === "true",
      mobileTourDone: mobileCompleted === "true" || mobileSkipped === "true",
    };
  }, []);

  // Save to local storage
  const saveToLocalStorage = useCallback((key: string, value: string) => {
    if (typeof window !== "undefined") {
      localStorage.setItem(key, value);
    }
  }, []);

  // Initialize and determine flow state
  useEffect(() => {
    const initializeFlow = async () => {
      // Wait for mobile detection
      if (isMobile === null) return;

      // Wait for session to be determined (but not necessarily authenticated)
      if (sessionStatus === "loading") return;

      try {
        // Check database for authenticated users
        if (sessionStatus === "authenticated" && session?.user) {
          const prefs = await getUserPreferences();

          if (prefs.error) {
            console.error("Error fetching user preferences:", prefs.error);
            // Fall back to localStorage on error
            const localPrefs = checkLocalStorage();
            if (localPrefs) {
              determineFlowState(
                isMobile
                  ? localPrefs.mobileTourDone
                  : localPrefs.desktopTourDone
              );
            } else {
              setFlowState(isMobile ? "show-mobile-tour" : "show-desktop-tour");
            }
            return;
          }

          const tourDone = isMobile
            ? !!prefs.mobileTourCompletedAt
            : !!prefs.desktopTourCompletedAt;

          // Also check localStorage
          const localPrefs = checkLocalStorage();

          determineFlowState(
            tourDone ||
              (isMobile
                ? localPrefs?.mobileTourDone
                : localPrefs?.desktopTourDone) ||
              false
          );
        } else {
          // Anonymous user - use localStorage only
          const localPrefs = checkLocalStorage();
          if (localPrefs) {
            determineFlowState(
              isMobile
                ? localPrefs.mobileTourDone
                : localPrefs.desktopTourDone
            );
          } else {
            setFlowState(isMobile ? "show-mobile-tour" : "show-desktop-tour");
          }
        }

        setIsInitialized(true);
      } catch (error) {
        console.error("Error initializing first-time user experience:", error);
        // On error, fall back to localStorage
        const localPrefs = checkLocalStorage();
        if (localPrefs) {
          determineFlowState(
            isMobile ? localPrefs.mobileTourDone : localPrefs.desktopTourDone
          );
        } else {
          setFlowState(isMobile ? "show-mobile-tour" : "show-desktop-tour");
        }
        setIsInitialized(true);
      }
    };

    const determineFlowState = (tourDone: boolean) => {
      if (!tourDone) {
        setFlowState(isMobile ? "show-mobile-tour" : "show-desktop-tour");
      } else {
        setFlowState("complete");
      }
    };

    initializeFlow();
  }, [sessionStatus, session, isMobile, checkLocalStorage]);

  // Handle tour completion
  const handleTourComplete = useCallback(() => {
    setFlowState("complete");
  }, []);

  // Don't render anything while loading
  if (flowState === "loading" || !isInitialized) {
    return <>{children}</>;
  }

  return (
    <>
      {children}

      {/* Mobile Tour - Always rendered to listen for restart events */}
      <MobileWelcomeTour
        shouldRun={flowState === "show-mobile-tour"}
        onComplete={handleTourComplete}
      />

      {/* Desktop Tour - Always rendered to listen for restart events */}
      {/* The tour only runs when triggered by shouldRun or restart event */}
      <WelcomeTour
        shouldRun={flowState === "show-desktop-tour"}
        onComplete={handleTourComplete}
      />
    </>
  );
}

/**
 * Export restart functions for use from user menu
 */
export { restartMobileTour } from "./mobile-welcome-tour";
export { restartWelcomeTour } from "./welcome-tour";
